import React from 'react';
import { CartProductItem } from '../models/cart_model';
import ReactGA from 'react-ga4';

declare global {
  interface Window {
    gtag: any;
  }
}

// const production = process.env.REACT_APP_PROD === 'true';
const production = true;

let gaInitialized = false;
let gaOptedOut = false;

const GA_OPT_OUT_KEY = 'ga_opt_out';

const isOptedOut = (): boolean =>
  localStorage.getItem(GA_OPT_OUT_KEY) === 'true';

const initGA = () => {
  if (gaInitialized || !production || !process.env.REACT_APP_GA_MEASUREMENT_ID) return;
  if (isOptedOut()) return;
  ReactGA.initialize(process.env.REACT_APP_GA_MEASUREMENT_ID);
  gaInitialized = true;
  gaOptedOut = false;
};

const disableGA = () => {
  gaOptedOut = true;
  localStorage.setItem(GA_OPT_OUT_KEY, 'true');
  // Google's official opt-out flag — suppresses all hits if set before the first event
  (window as any)[`ga-disable-${process.env.REACT_APP_GA_MEASUREMENT_ID}`] = true;
};

// Initialize immediately on load (tacit consent — opt-out model)
initGA();

if (typeof window !== 'undefined') {
  window.addEventListener('cookie-consent-updated', ((e: CustomEvent) => {
    if (e.detail && e.detail.analytics) {
      localStorage.removeItem(GA_OPT_OUT_KEY);
      (window as any)[`ga-disable-${process.env.REACT_APP_GA_MEASUREMENT_ID}`] = false;
      initGA();
    } else {
      disableGA();
    }
  }) as EventListener);
}

export type EcommerceData = {
  name: string;
  id: string;
  revenue: string;
  subscription: string;
  sku: string;
  category: string;
};

export const AnalyticsContext = React.createContext({
  event: async (data: {
    action: string;
    value: number;
    products?: CartProductItem[];
    transaction_id?: string;
    currency?: string;
    shipping?: number;
    tax?: number;
    payment_type?: string;
  }) => {},
  view: (location: string) => {},
  ecommerce: (data: EcommerceData) => {},
  customEvent: (data: {
    event_name: string;
    event_category: string;
    event_label?: string;
    value?: number;
    custom_parameters?: Record<string, any>;
  }) => {},
});

const AnalyticsContextProvider: React.FC = ({ children }) => {
  const event = async (data: {
    action: string;
    value: number;
    products?: CartProductItem[];
    transaction_id?: string;
    currency?: string;
    shipping?: number;
    tax?: number;
    payment_type?: string;
  }) => {
    // if (!production) {
    //   return false;
    // }

    const eventData: any = {
      value: data.value,
      currency: data.currency || 'MXN',
      action: data.action,
    };

    // Add transaction_id if provided
    if (data.transaction_id) {
      eventData.transaction_id = data.transaction_id;
    }

    // Add shipping and tax if provided
    if (data.shipping !== undefined) {
      eventData.shipping = data.shipping;
    }
    if (data.tax !== undefined) {
      eventData.tax = data.tax;
    }

    // Add payment type if provided
    if (data.payment_type) {
      eventData.payment_type = data.payment_type;
    }

    // Add items if products are provided
    if (data.products && data.products.length > 0) {
      eventData.items = data.products.map((product) => ({
        item_id: product.id,
        item_name: product.title, /// is product.descp1 in cart collection
        item_category: product.grpsbi?.[2] || 'General', // Using the third element of grpsbi as category
        quantity: product.cant,
        price: product.price,
      }));
    }

    debugger;
    ReactGA.event(eventData);
  };

  const pageview = (location: string) => {
    if (!production || !location || !gaInitialized || gaOptedOut) return false;
    ReactGA.send({ hitType: 'pageview', page: location });
  };

  const ecommerce = (data: EcommerceData) => {
    if (!production) return false;
    if (!data) return false;
    ReactGA.event('purchase', {
      transaction_id: data.id,
      affiliation: 'mathasa.mx',
      value: data.revenue,
      currency: 'MXN',
      shipping: 0,
      items: [
        {
          item_id: data.sku, // SKU/code
          item_name: data.subscription, // Product name. Required
          item_category: data.category, // Category or variation
          quantity: 1,
          price: data.revenue,
        },
      ],
    });
  };

  const customEvent = (data: {
    event_name: string;
    event_category: string;
    event_label?: string;
    value?: number;
    custom_parameters?: Record<string, any>;
  }) => {
    if (!production) return false;

    const eventData: any = {
      event_category: data.event_category,
    };

    if (data.event_label) {
      eventData.event_label = data.event_label;
    }

    if (data.value !== undefined) {
      eventData.value = data.value;
    }

    if (data.custom_parameters) {
      Object.assign(eventData, data.custom_parameters);
    }

    ReactGA.event(data.event_name, eventData);
  };

  return (
    <AnalyticsContext.Provider
      value={{
        event: event,
        view: pageview,
        ecommerce,
        customEvent,
      }}
    >
      {children}
    </AnalyticsContext.Provider>
  );
};

export default AnalyticsContextProvider;
